import { ComponentType, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { ReactComponent as PencilIcon } from '../../../assets/16x16/edit-3.svg';
import { ReactComponent as BinocularsIcon } from '../../../assets/16x16/binoculars.svg';
import Menu, { MenuOption } from '../../Menu';
import SvgIcon from '../../SvgIcon';
import { CHAT_MODES } from '../constants';
import { ChatMode } from '../types';
import { MenuPillButton, MenuPillChevron } from './menuPill';

const askStyles = css`
  background: ${({ theme }) => theme.palette.common.lightBlue4};
  color: ${({ theme }) => theme.palette.common.blue7};

  &:hover {
    background: ${({ theme }) => theme.palette.common.lightBlue4};
    border: none;
    opacity: 0.85;
  }
`;

const editStyles = css`
  background: ${({ theme }) => theme.palette.common.lightPink};
  color: ${({ theme }) => theme.palette.common.magenta};

  &:hover {
    background: ${({ theme }) => theme.palette.common.lightMagenta};
    border: none;
  }
`;

const ModeButton = styled(MenuPillButton)<{ $mode: ChatMode }>`
  ${({ $mode }) => ($mode === 'ask' ? askStyles : editStyles)}
`;

const MODE_ICONS: Record<ChatMode, ComponentType> = {
  ask: BinocularsIcon,
  agent: PencilIcon,
};

const MODE_COLORS: Record<ChatMode, string> = {
  ask: 'blue7',
  agent: 'magenta',
};

const renderModeIcon = (mode: ChatMode) => {
  const Icon = MODE_ICONS[mode];
  return (
    <SvgIcon strokeColor={MODE_COLORS[mode]}>
      <Icon />
    </SvgIcon>
  );
};

type Props = {
  availableModes: ChatMode[];
  selectedMode: ChatMode;
  onChange: (mode: ChatMode) => void;
};

const ModeSelector = ({ availableModes, selectedMode, onChange }: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  const menuOptions: MenuOption[] = useMemo(
    () =>
      CHAT_MODES.filter((m) => availableModes.includes(m.key)).map((m) => ({
        value: m.key,
        label: m.label,
        icon: renderModeIcon(m.key),
      })),
    [availableModes],
  );

  const selectedLabel = CHAT_MODES.find((m) => m.key === selectedMode)?.label;

  if (availableModes.length <= 1) {
    return null;
  }

  return (
    <Menu
      menuButton={
        <ModeButton
          size="extraSmall"
          $mode={selectedMode}
          startIcon={renderModeIcon(selectedMode)}
          endIcon={<MenuPillChevron $isOpen={isOpen} />}
        >
          {selectedLabel}
        </ModeButton>
      }
      options={menuOptions}
      selectedOptions={[selectedMode]}
      onChange={(key) => {
        onChange(key as ChatMode);
        setIsOpen(false);
      }}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      closeOnChange
      anchorOrigin={{ vertical: -8, horizontal: 'left' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    />
  );
};

export default ModeSelector;
